import { createRouter, createWebHistory, RouteLocationNormalized, RouteRecordRaw } from 'vue-router'
import { i18n } from '@/config/i18n'
import { computed, ComputedRef } from 'vue'
import store from '@/store'
import { Actions } from '@/store/enums/StoreEnums'
import { BRAND_CONFIG } from '@/config/brand'
import {
  captureAuthenticationRedirectContextFromRoute,
  saveInternalRedirectPath
} from '@/services/AuthenticationRedirectContext'
import {
  readCandidatePostLogoutRedirectUri,
  consumePostLogoutRedirectTarget
} from '@/services/PostLogoutRedirect'

const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    name: 'home',
    component: () => import(/* webpackChunkName: "home" */ '../views/LoginView.vue'),
    meta: {
      title: computed(() => i18n.global.t('meta.titles.home'))
    }
  },
  {
    path: '/first-connection',
    name: 'first-connection',
    component: () => import(/* webpackChunkName: "first-login" */ '../views/FirstConnectionView.vue'),
    meta: {
      title: computed(() => i18n.global.t('meta.titles.firstConnection'))
    }
  },
  {
    path: '/first-connection/:token',
    name: 'first-connection-token',
    component: () => import(/* webpackChunkName: "first-login" */ '../views/FirstConnectionForm.vue'),
    meta: {
      title: computed(() => i18n.global.t('meta.titles.firstConnection'))
    }
  },
  {
    path: '/oauth/authorize',
    name: 'oauth-authorize',
    component: { render: (h: () => any) => h() },
    beforeEnter: (to, from, next) => {
      // La requête d'autorisation a déjà été capturée par `router.beforeEach`, qui
      // s'exécute avant les `beforeEnter`. Rien à mémoriser ici, et rien à recopier
      // dans l'URL : la destination vit dans le contexte, pas dans la query string.
      next({ name: 'oauth-redirect', replace: true })
    }
  },
  {
    path: '/oauth/redirect',
    name: 'oauth-redirect',
    component: () => import(/* webpackChunkName: "oauth-redirect" */ '../views/AuthorizationRedirectView.vue'),
    meta: {
      title: computed(() => i18n.global.t('meta.titles.oauthRedirect')),
      requiresAuthentication: true
    }
  },
  {
    path: '/auth/adfs/callback',
    name: 'adfs-callback',
    component: () => import(/* webpackChunkName: "adfs-callback" */ '../views/AdfsCallbackView.vue'),
    meta: {
      title: computed(() => i18n.global.t('meta.titles.home')),
      // Marque la route de retour du fournisseur d'identité : son `state` ne doit
      // jamais être pris pour celui d'une application appelante.
      isIdentityProviderCallback: true
    }
  },
  {
    path: '/services',
    name: 'services',
    component: () => import(/* webpackChunkName: "services" */ '../views/ServicesView.vue'),
    meta: {
      title: computed(() => i18n.global.t('meta.titles.services')),
      requiresAuthentication: true
    }
  },
  {
    path: '/connect-as',
    name: 'connect-as',
    component: () => import(/* webpackChunkName: "connect-as" */ '../views/ConnectAsView.vue'),
    meta: {
      title: computed(() => i18n.global.t('authentication.connectAs.title')),
      requiresAuthentication: true
    }
  },
  {
    // Chemin distinct de `/connect-as` : vue-router compare les chemins en entier, il n'y a
    // pas de collision, quel que soit l'ordre de déclaration.
    path: '/connect-as/exit',
    name: 'connect-as-exit',
    component: () => import(/* webpackChunkName: "connect-as-exit" */ '../views/ExitConnectAsView.vue'),
    meta: {
      title: computed(() => i18n.global.t('authentication.connectAs.title')),
      requiresAuthentication: true
    }
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: () => import(/* webpackChunkName: "forgot-password" */ '../views/ForgotPasswordView.vue'),
    meta: {
      title: computed(() => i18n.global.t('meta.titles.forgotPassword'))
    }
  },
  {
    path: '/reset-password/:token',
    name: 'reset-password',
    component: () => import(/* webpackChunkName: "reset-password" */ '../views/ForgotPasswordForm.vue'),
    meta: {
      title: computed(() => i18n.global.t('meta.titles.resetPassword'))
    }
  },
  {
    path: '/magic-link/:token',
    name: 'magic-link',
    component: () => import(/* webpackChunkName: "magic-link" */ '../views/MagicLinkForm.vue'),
    meta: {
      title: computed(() => i18n.global.t('meta.titles.magicLink'))
    }
  },
  {
    path: '/logout',
    name: 'logout',
    component: { render: (h: () => any) => h() },
    beforeEnter: async (to, from, next) => {
      // Le candidat part avec l'appel de déconnexion lui-même : c'est sa réponse qui
      // porte le `redirect_url` validé, mémorisé par `Actions.LOGOUT_USER`.
      const postLogoutRedirectUriCandidate = readCandidatePostLogoutRedirectUri(to)

      const didRedirectToIdentityProvider = await store.dispatch(Actions.LOGOUT_USER, {
        postLogoutRedirectUri: postLogoutRedirectUriCandidate
      })

      if (didRedirectToIdentityProvider) {
        // La navigation est déjà prise en charge par `window.location.href` : rien
        // d'autre à faire ici que de satisfaire le routeur.
        next({ name: 'home' })
        return
      }

      const postLogoutRedirectTarget = consumePostLogoutRedirectTarget()
      if (postLogoutRedirectTarget !== null) {
        window.location.href = postLogoutRedirectTarget
        return
      }

      next({ name: 'home' })
    }
  },
  {
    path: '/logout/callback',
    name: 'logout-callback',
    component: { render: (h: () => any) => h() },
    beforeEnter: (to, from, next) => {
      // Retour d'ADFS après sa propre déconnexion : la session locale est déjà
      // nettoyée depuis la route `/logout`, il ne reste qu'à honorer la destination
      // mémorisée avant ce détour, si elle existe encore.
      const postLogoutRedirectTarget = consumePostLogoutRedirectTarget()
      if (postLogoutRedirectTarget !== null) {
        window.location.href = postLogoutRedirectTarget
        return
      }

      next({ name: 'home' })
    }
  },
  {
    path: '/error',
    name: 'error',
    component: () => import(/* webpackChunkName: "error" */ '../views/ErrorView.vue'),
    meta: {
      title: computed(() => i18n.global.t('meta.titles.error') || 'Erreur')
    }
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import(/* webpackChunkName: "not-found" */ '../views/NotFoundView.vue'),
    meta: {
      title: computed(() => i18n.global.t('meta.titles.notFound') || 'Page introuvable')
    }
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
})

/**
 * Met à jour le titre du document à partir de la route atteinte.
 */
function updateDocumentTitle (to: RouteLocationNormalized): void {
  const brandTitle = BRAND_CONFIG.documentTitle ?? i18n.global.t('global.title')
  if (to.meta.title && (<ComputedRef<string>>to.meta.title).value) {
    document.title = (to.meta.title ? (to.meta.title as ComputedRef<string>).value + ' - ' : '') + brandTitle
  } else {
    document.title = (to.meta.title ? to.meta.title + ' - ' : '') + brandTitle
  }
}

router.beforeEach((to, from, next) => {
  updateDocumentTitle(to)

  // Capturer la requête d'autorisation quand l'URL en porte une. Sans effet sur la
  // route de retour du fournisseur d'identité : c'est ce qui empêche son `state` de
  // se substituer à celui de l'application appelante.
  captureAuthenticationRedirectContextFromRoute(to)

  // Vérification de l'authentification
  const requiresAuthentication = to.matched.some(record => record.meta.requiresAuthentication)
  const isLoggedIn = store.getters.isLoggedIn

  if (requiresAuthentication && !isLoggedIn) {
    // Mémoriser la page demandée dans le contexte, et non dans la query string :
    // les paramètres de redirection n'ont pas à circuler dans une URL publique.
    saveInternalRedirectPath(to.fullPath)
    next({ name: 'home' })
  } else if (to.name === 'home' && isLoggedIn) {
    // La destination est décidée par le contexte, pas par l'URL.
    next({ name: 'services' })
  } else {
    next()
  }
})

export default router
